import NotFound from "@/assets/images/not-found.png";
import AccountLayout from "@/layouts/AccountLayout";
import {
  DotsThreeVertical,
  Pen,
  Pencil,
  Plus,
  Trash,
} from "@phosphor-icons/react";
import { useRouter } from "next/router";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import AppContext from "@/AppContext";
import DummyLogo from "@/assets/images/lucidity-placeholder.png";
import SubNavigationMobile from "@/components/elements/SubNavigationMobile";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import WishlistAddNew from "@/components/WishlistAddNew";
import useAuth from "@/hooks/auth";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  createWishlistCategory,
  deleteWishlist,
  deleteWishlistCategory,
  editWishlistCategory,
  getWishlist,
  getWishlistCategory,
  moveWishlist,
} from "@/services/api/Wishlist";
import { ArrowLeft } from "@phosphor-icons/react/dist/ssr";
import dayjs from "dayjs";
import Image from "next/legacy/image";
import Link from "next/link";
import { useContext, useEffect, useRef, useState } from "react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";

const CATEGORY_MAP = {
  0: "Content",
  1: "Startup",
  2: "Venture Capital",
};

const LINK_MAP = {
  0: "",
  1: "startups",
  2: "investors",
};

const CONTENT_TYPE_MAP = {
  News: "news",
  Articles: "articles",
  Videos: "videos",
  "Podcast Episodes": "podcasts-masterclasses",
  Opinions: "opinions",
  Spotlights: "spotlights",
  Infobytes: "infobytes",
  "Special Reports": "reports",
};

function RenameDirectoryContent({
  editedNameCat,
  setEditedNameCat,
  editCategoryName,
  handleClose,
}) {
  return (
    <>
      <h2 className='text-lg font-medium'>Rename Directory</h2>
      <p className='text-sm text-background/60'>
        {`Make changes to your watchlist name so you can identify it. Click save
        when you're done.`}
      </p>

      <div className='flex items-center py-4 gap-x-5'>
        <Input
          defaultValue=''
          className='flex-1 bg-white text-background border-background/20 focus-visible:ring-0'
          value={editedNameCat}
          onChange={(e) => setEditedNameCat(e.target.value)}
        />
      </div>

      <div className='flex justify-end gap-2'>
        <Button
          onClick={handleClose}
          variant='outline'
          className='hidden w-full bg-white rounded-lg lg:flex lg:w-auto lg:mt-0 border-black/20 hover:bg-gray-50'
        >
          Cancel
        </Button>
        <Button
          type='submit'
          variant='secondary'
          onClick={editCategoryName}
          className='w-full rounded-lg lg:w-auto'
        >
          Save changes
        </Button>
      </div>
    </>
  );
}

function DeleteCategoryContent({ onCancelDeleteCategory, onDeleteCategory }) {
  return (
    <div>
      <h2 className='text-lg font-medium'>Are you absolutely sure?</h2>
      <p className='mt-1 text-sm text-background/60'>
        This action cannot be undone. This will permanently delete your
        directory and its content.
      </p>

      <div className='flex justify-end gap-2 pt-4'>
        <Button
          onClick={onCancelDeleteCategory}
          variant='outline'
          className='hidden w-full bg-white rounded-lg lg:flex lg:w-auto lg:mt-0 border-black/20 hover:bg-gray-50'
        >
          Cancel
        </Button>
        <Button
          type='submit'
          variant='secondary'
          onClick={onDeleteCategory}
          className='w-full bg-red-500 rounded-lg lg:w-auto'
        >
          Delete Watchlist
        </Button>
      </div>
    </div>
  );
}

export default function Profile() {
  const { user } = useAuth({ middleware: "auth" });
  const router = useRouter();
  //   const { redirect_to } = router.query;

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteCategoryDialogOpen, setIsDeleteCategoryDialogOpen] =
    useState(false);
  const [isDeleteItemDialogOpen, setIsDeleteItemDialogOpen] = useState(false);
  const [userWishlist, setUserWishlist] = useState([]);
  const [wishlistCategories, setWishlistCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedWishlist, setSelectedWishlist] = useState(null);
  const [openWishlistAction, setOpenWishlistAction] = useState(false);
  const [openRenameCategory, setOpenRenameCategory] = useState(false);
  const [openedCatId, setOpenedCatId] = useState(null);
  const [editedNameCat, setEditedNameCat] = useState("");
  const triggerRef = useRef(null);
  const createNewRef = useRef(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { state } = useContext(AppContext);

  const { planSubscription, loading, refreshUserSubscription } = state;

  const hasSubscription =
    planSubscription && planSubscription.has_active_subscription;

  const getUserWishlist = async () => {
    const res = await getWishlist({
      per_page: 1000,
      excluded_type: 0,
    });
    setUserWishlist(res.data.data);
  };

  const getWishlistCategories = async () => {
    const res = await getWishlistCategory();
    setWishlistCategories(res.data);
  };

  useEffect(() => {
    getUserWishlist();
    getWishlistCategories();
  }, []);

  const handleDeleteWishlist = (wishlist) => {
    setSelectedWishlist(wishlist);
    setIsDeleteItemDialogOpen(true);
  };

  const onDeleteWishlist = async () => {
    await deleteWishlist(selectedWishlist.id);
    setSelectedWishlist(null);
    setIsDeleteItemDialogOpen(false);
    getUserWishlist();
  };

  const onCancelDelete = () => {
    setSelectedWishlist(null);
    setIsDeleteItemDialogOpen(false);
  };

  const handleChange = (e) => {
    setOpenWishlistAction(e);
  };

  const handleOpenRename = (e, category) => {
    e.stopPropagation();
    e.preventDefault();
    // triggerRef.current?.click();
    setTimeout(() => {
      setOpenedCatId(category.id);
      setEditedNameCat(category.name);
      setIsRenameDialogOpen(true);
    }, 100);
  };

  const handleOpenDeleteCategory = (e, category) => {
    e.stopPropagation();
    e.preventDefault();
    setIsDeleteCategoryDialogOpen(true);
    setOpenedCatId(category.id);
  };

  // Function to handle My Wishlist click without propagating
  const handleMyWishlistClick = (event, wish_id, cat_id) => {
    event.stopPropagation();
    onMoveWishlist(wish_id, cat_id);
    // Add navigation or any other logic for My Wishlist here
  };

  const onMoveWishlist = async (wish_id, cat_id) => {
    const res = await moveWishlist(wish_id, {
      category_id: cat_id,
    });
    getUserWishlist();
  };

  const handleCreateNew = (e, w) => {
    e.stopPropagation();
    e.preventDefault();
    createNewRef.current?.click();
    setTimeout(() => {
      setIsAddDialogOpen(true); // Open the add dialog
      setSelectedWishlist(w);
    }, 100);
    setOpenWishlistAction(false);
  };

  const addWishlistCategory = async (payload) => {
    try {
      const res = await createWishlistCategory(payload);
      if (selectedWishlist) {
        await onMoveWishlist(selectedWishlist.id, res.data.id);
      }
      getWishlistCategories();
      setIsAddDialogOpen(false);
    } catch (error) {
      console.log(error);
    }
    setSelectedWishlist(null);
  };

  const editCategoryName = async () => {
    try {
      if (openedCatId) {
        const res = await editWishlistCategory(openedCatId, {
          name: editedNameCat,
        });

        // Optional: you may not need this if you're updating locally
        getWishlistCategories();

        // ✅ Fix: update selectedCategory if it's the one renamed
        if (selectedCategory?.id === openedCatId) {
          setSelectedCategory((prev) =>
            prev ? { ...prev, name: editedNameCat } : null
          );
        }

        setIsRenameDialogOpen(false);
      }
    } catch (error) {
      console.log(error);
    }

    setEditedNameCat("");
    setOpenedCatId(null);
  };

  const onDeleteCategory = async () => {
    try {
      if (openedCatId) {
        const res = await deleteWishlistCategory(openedCatId);
        getWishlistCategories();
        setIsRenameDialogOpen(false);
      }
    } catch (error) {
      console.log(error);
    }
    setOpenedCatId(null);
    setIsDeleteCategoryDialogOpen(false);
    setShowDetail(false);
  };

  const onCancelDeleteCategory = () => {
    setIsDeleteCategoryDialogOpen(false);
    setOpenedCatId(null);
  };

  const handleOnlyCreateCategory = () => {
    if (selectedWishlist) {
      setSelectedWishlist(false);
    }
    setIsAddDialogOpen(true);
  };

  const isMobile = useIsMobile();

  const [showDetail, setShowDetail] = useState(false);
  const [selectedListItems, setSelectedListItems] = useState([]);

  // Handler for clicking "My Watchlist"
  const handleMyWatchlistClick = () => {
    setSelectedCategory(null);
    setSelectedListItems(userWishlist.filter((w) => !w.category));
    setShowDetail(true);
  };

  // Handler for clicking a category
  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setSelectedListItems(
      userWishlist.filter((w) => w.category?.id === category.id)
    );
    setShowDetail(true);
  };

  return (
    <AccountLayout title={"Profile | Lucidity Insights"}>
      <div className='container'>
        {/* Mobile List */}
        {isMobile && !showDetail && (
          <>
            <SubNavigationMobile label='Watchlists' />
            <button
              type='button'
              onClick={handleOnlyCreateCategory}
              className='fixed z-10 flex items-center gap-2 px-5 py-3 pl-4 text-sm font-semibold leading-none tracking-tight -translate-x-1/2 bg-white rounded-full whitespace-nowrap text-background bottom-4 left-1/2'
            >
              <Plus weight='bold' />
              Create New Watchlist
            </button>
            <div className='flex p-1 mt-2 rounded-lg bg-white/10'>
              <Link
                href='/account/watchlist'
                className='w-full py-2 text-sm font-semibold text-center rounded-lg bg-background'
              >
                Watchlists
              </Link>
              <Link
                href='/account/bookmarks'
                className='w-full py-2 text-sm font-semibold text-center rounded-lg text-white/40'
              >
                Bookmarks
              </Link>
            </div>
            <div className='pt-5 -my-3 divide-y divide-dashed divide-white/10'>
              {/* My Watchlist */}
              <div
                onClick={handleMyWatchlistClick}
                className='py-3 cursor-pointer'
              >
                <div className='flex items-center justify-between w-full gap-2 font-semibold'>
                  <div className='flex items-center gap-2'>
                    My Watchlist{" "}
                    {userWishlist.filter((w) => !w.category).length > 0 && (
                      <span className='inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full bg-white/20'>
                        {userWishlist.filter((w) => !w.category).length}
                      </span>
                    )}
                  </div>
                </div>
                <div className='flex items-center gap-2 mt-3'>
                  <div className='flex -space-x-2'>
                    {userWishlist
                      .filter((w) => !w.category)
                      .slice(0, 7)
                      .map((wishlist, index) => (
                        <div
                          className='relative flex-shrink-0 overflow-hidden rounded-full aspect-square size-12 ring-4 ring-background'
                          key={index}
                        >
                          <Image
                            src={
                              !wishlist.contentType
                                ? wishlist.object_detail?.logo_url || DummyLogo
                                : wishlist.contentType.name ===
                                  "Special Reports"
                                ? wishlist.object_detail?.cover_image_url ||
                                  DummyLogo
                                : wishlist.object_detail?.thumbnail_url ||
                                  DummyLogo
                            }
                            alt=''
                            className='absolute inset-0 object-cover'
                            layout='fill'
                          />
                        </div>
                      ))}
                  </div>
                  {userWishlist.filter((w) => !w.category).length > 7 && (
                    <span className='text-xs font-medium opacity-50'>
                      +{userWishlist.filter((w) => !w.category).length - 7} more
                    </span>
                  )}
                </div>
              </div>
              {/* Categories */}
              {wishlistCategories.map((category) => (
                <div
                  onClick={() => handleCategoryClick(category)}
                  key={category.id}
                  className='py-3 cursor-pointer'
                >
                  <div className='flex items-center justify-between w-full gap-2 font-semibold'>
                    <div className='flex items-center gap-2'>
                      {category.name}{" "}
                      {userWishlist.filter(
                        (w) => w.category?.id === category.id
                      ).length > 0 && (
                        <span className='inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full bg-white/20'>
                          {
                            userWishlist.filter(
                              (w) => w.category?.id === category.id
                            ).length
                          }
                        </span>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          type='button'
                          className='-mr-1'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DotsThreeVertical
                            weight='bold'
                            className='text-lg'
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem
                          onClick={(e) => handleOpenRename(e, category)}
                        >
                          <Pen weight='bold' className='w-4 h-4 mr-2' />
                          <span>Rename</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => handleOpenDeleteCategory(e, category)}
                        >
                          <Trash weight='bold' className='w-4 h-4 mr-2' />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  {userWishlist.filter((w) => w.category?.id === category.id)
                    .length > 0 && (
                    <div className='flex items-center gap-2 mt-3'>
                      <div className='flex -space-x-2'>
                        {userWishlist
                          .filter((w) => w.category?.id === category.id)
                          .slice(0, 7)
                          .map((wishlist, index) => (
                            <div
                              className='relative flex-shrink-0 overflow-hidden rounded-full aspect-square size-12 ring-4 ring-background'
                              key={index}
                            >
                              <Image
                                src={
                                  !wishlist.contentType
                                    ? wishlist.object_detail?.logo_url ||
                                      DummyLogo
                                    : wishlist.contentType.name ===
                                      "Special Reports"
                                    ? wishlist.object_detail?.cover_image_url ||
                                      DummyLogo
                                    : wishlist.object_detail?.thumbnail_url ||
                                      DummyLogo
                                }
                                alt=''
                                className='absolute inset-0 object-cover'
                                layout='fill'
                              />
                            </div>
                          ))}
                      </div>
                      {userWishlist.filter(
                        (w) => w.category?.id === category.id
                      ).length > 7 && (
                        <span className='text-xs font-medium opacity-50'>
                          +
                          {userWishlist.filter(
                            (w) => w.category?.id === category.id
                          ).length - 7}{" "}
                          more
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
        {/* Detailed List View */}
        {showDetail && (
          <div>
            <div className='sticky top-0 z-10 flex items-center justify-between w-full py-2 text-sm bg-background/90 backdrop-blur-md'>
              <button
                type='button'
                onClick={() => setShowDetail(false)}
                className='inline-flex items-center justify-center flex-shrink-0 transition-colors rounded-full size-9 bg-white/10'
              >
                <ArrowLeft weight='bold' className='text-lg' />
              </button>
              {selectedCategory && (
                <div className='flex items-center divide-x divide-white/10'>
                  <button
                    type='button'
                    onClick={(e) => handleOpenRename(e, selectedCategory)}
                    className='flex items-center gap-1.5 font-medium'
                  >
                    <Pencil weight='bold' className='size-4' />
                    Rename
                  </button>
                  <button
                    type='button'
                    onClick={(e) =>
                      handleOpenDeleteCategory(e, selectedCategory)
                    }
                    className='pl-2.5 ml-2.5 text-red-500'
                  >
                    <Trash weight='bold' className='size-4' />
                  </button>
                </div>
              )}
            </div>
            <h2 className='pt-6 text-3xl font-bold tracking-tight'>
              {selectedCategory ? selectedCategory.name : "My Watchlist"}
            </h2>

            <div className='grid pt-8 pb-8 -my-3 divide-y divide-dashed divide-white/10'>
              {selectedListItems.length > 0 ? (
                selectedListItems.map((wishlist, index) => (
                  <button
                    key={index}
                    type='button'
                    onClick={() =>
                      router.push(
                        wishlist.contentType
                          ? `/${CONTENT_TYPE_MAP[wishlist.contentType.name]}/${
                              wishlist.object_detail?.slug
                            }`
                          : `/${LINK_MAP[wishlist.type]}/${
                              wishlist.object_detail?.slug
                            }-${wishlist.object_detail?.id}`
                      )
                    }
                    className='flex items-start gap-3 py-3 text-left'
                  >
                    <div className='relative overflow-hidden rounded-full aspect-square size-10'>
                      <Image
                        src={
                          !wishlist.contentType
                            ? wishlist.object_detail?.logo_url || DummyLogo
                            : wishlist.contentType.name === "Special Reports"
                            ? wishlist.object_detail?.cover_image_url ||
                              DummyLogo
                            : wishlist.object_detail?.thumbnail_url || DummyLogo
                        }
                        alt=''
                        className='absolute inset-0 object-cover'
                        layout='fill'
                      />
                    </div>
                    <div className='flex-1'>
                      <h3 className='text-base font-semibold leading-tight'>
                        {wishlist.object_detail?.title ||
                          wishlist.object_detail?.name ||
                          "Untitled"}
                      </h3>

                      {wishlist.object_detail?.country && (
                        <p className='mt-1 text-xs text-white/40'>
                          {wishlist.object_detail?.city && (
                            <span>{wishlist.object_detail.city.name}, </span>
                          )}
                          {wishlist.object_detail.country.country_name}
                        </p>
                      )}
                    </div>
                  </button>
                ))
              ) : (
                <div className='flex flex-col items-center justify-center p-8 text-center border border-dashed rounded-xl'>
                  <div className='relative size-28'>
                    <Image
                      objectFit='cover'
                      src={NotFound.src}
                      alt='NotFound Image'
                      className='object-cover'
                      layout='fill'
                    />
                  </div>
                  <p className='mt-3 text-lg font-semibold'>
                    Oops, It is Empty
                  </p>
                  <p className='mt-1.5 text-sm opacity-40'>
                    Explore our massive database of startups, investors, and
                    more to add content to your watchlist.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Desktop */}
        <div className='hidden lg:flex flex-wrap lg:min-h-[80vh] gap-x-8'>
          <div className='w-full lg:flex-1 lg:rounded-md p-4 lg:p-6 border-y border-x border-y-white/10 border-x-white/[0.03] bg-gradient-to-t from-white/[0.05] to-white/[0.07] shadow-md'>
            <div className='flex flex-col h-full'>
              <div className='flex items-center pb-3 mb-5 border-b gap-x-3'>
                <p className='text-2xl font-semibold'>
                  {selectedCategory ? selectedCategory.name : "My Watchlist"}
                </p>
              </div>
              <div className='flex-1'>
                {/* Enable if has the data */}
                <div className='-mx-6'>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className='pl-6'>Content</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Added Date</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userWishlist
                        .filter((w) => w.category?.id === selectedCategory?.id)
                        .map((wishlist, index) => (
                          <TableRow key={wishlist.id}>
                            <TableCell className='pl-6 font-medium max-w-[250px]'>
                              <Link
                                legacyBehavior
                                href={
                                  wishlist.contentType
                                    ? `/${
                                        CONTENT_TYPE_MAP[
                                          wishlist.contentType.name
                                        ]
                                      }/${wishlist.object_detail?.slug}`
                                    : `/${LINK_MAP[wishlist.type]}/${
                                        wishlist.object_detail?.slug
                                      }-${wishlist.object_detail?.id}`
                                }
                                passHref
                                key={index}
                              >
                                <a className='flex py-3 space-x-4 group'>
                                  <div className='flex-shrink-0 w-[40px] h-[40px]'>
                                    <Image
                                      width={40}
                                      height={40}
                                      objectFit='cover'
                                      src={
                                        !wishlist.contentType
                                          ? wishlist.object_detail?.logo_url ||
                                            DummyLogo
                                          : wishlist.contentType.name ===
                                            "Special Reports"
                                          ? wishlist.object_detail
                                              ?.cover_image_url || DummyLogo
                                          : wishlist.object_detail
                                              ?.thumbnail_url || DummyLogo
                                      }
                                      alt=''
                                      className='overflow-hidden rounded-[6px]'
                                    />
                                  </div>
                                  <div className='flex-1 min-w-0'>
                                    <p className='text-base font-medium text-white/60 group-hover:text-white'>
                                      {wishlist.title}
                                    </p>
                                    {wishlist.object_detail?.country && (
                                      <p className='text-xs text-white/40'>
                                        {wishlist.object_detail?.city && (
                                          <span>
                                            {wishlist.object_detail.city.name},{" "}
                                          </span>
                                        )}
                                        {
                                          wishlist.object_detail.country
                                            .country_name
                                        }
                                      </p>
                                    )}
                                  </div>
                                </a>
                              </Link>
                            </TableCell>
                            <TableCell className='w-[70px] font-medium whitespace-nowrap'>
                              <p>{CATEGORY_MAP[wishlist.type]}</p>
                              {wishlist.contentType && (
                                <p className='text-xs text-white/60'>
                                  {wishlist.contentType?.name}
                                </p>
                              )}
                            </TableCell>
                            <TableCell className='w-[70px] whitespace-nowrap'>
                              {dayjs(wishlist.added_at).format("DD MMM YYYY")}
                            </TableCell>
                            <TableCell className='w-[40px]'>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    type='button'
                                    className='-mr-1 translate-y-[3px]'
                                  >
                                    <DotsThreeVertical
                                      weight='bold'
                                      className='text-lg'
                                    />
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent className='w-[160px]'>
                                  <DropdownMenuSub
                                    open={openWishlistAction}
                                    onOpenChange={handleChange}
                                  >
                                    <DropdownMenuSubTrigger ref={createNewRef}>
                                      <span>Move to...</span>
                                    </DropdownMenuSubTrigger>
                                    <DropdownMenuPortal>
                                      <DropdownMenuSubContent className='w-[200px]'>
                                        <DropdownMenuItem
                                          onClick={(e) =>
                                            handleMyWishlistClick(
                                              e,
                                              wishlist.id,
                                              null
                                            )
                                          }
                                        >
                                          My Watchlist
                                        </DropdownMenuItem>
                                        {wishlistCategories
                                          ?.filter(
                                            (w) =>
                                              w.id !== wishlist.category?.id
                                          )
                                          .map((cat) => (
                                            <DropdownMenuItem
                                              key={cat.id}
                                              onClick={(e) =>
                                                handleMyWishlistClick(
                                                  e,
                                                  wishlist.id,
                                                  cat.id
                                                )
                                              }
                                            >
                                              {cat.name}
                                            </DropdownMenuItem>
                                          ))}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem
                                          onClick={(e) =>
                                            handleCreateNew(e, wishlist)
                                          }
                                        >
                                          <Plus
                                            weight='bold'
                                            className='mr-2'
                                          />
                                          <span>Create New Directory</span>
                                        </DropdownMenuItem>
                                      </DropdownMenuSubContent>
                                    </DropdownMenuPortal>
                                  </DropdownMenuSub>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() =>
                                      handleDeleteWishlist(wishlist)
                                    }
                                  >
                                    <Trash
                                      weight='bold'
                                      className='w-4 h-4 mr-2'
                                    />
                                    <span>Remove</span>
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
          <div className='order-first lg:order-last lg:h-full flex flex-col w-full lg:w-[220px] px-4 lg:px-0 sticky lg:top-32 mb-8 lg:mb-0'>
            <div className='flex items-center justify-between pb-3 mb-3 border-b'>
              <p className='text-base font-medium'>Your Watchlist Library</p>
              <button type='button' onClick={handleOnlyCreateCategory}>
                <Plus weight='bold' />
              </button>
            </div>
            <div className='flex-1'>
              <WishlistAddNew
                isOpen={isAddDialogOpen}
                setIsOpen={setIsAddDialogOpen}
                addWishlistCategory={addWishlistCategory}
                wishlistCategories={wishlistCategories}
                hasSubscription={hasSubscription}
              />
              <div className='flex flex-col items-start gap-1 -mx-2'>
                <div
                  onClick={() => setSelectedCategory(null)}
                  className={`w-full px-3 py-2 text-sm text-left transition-colors rounded-md cursor-pointer hover:bg-white/5 ${
                    !selectedCategory && "bg-white/5"
                  }`}
                >
                  My Watchlist{" "}
                  {userWishlist.filter((w) => !w.category).length > 0 && (
                    <span>
                      ({userWishlist.filter((w) => !w.category).length})
                    </span>
                  )}
                </div>
                {wishlistCategories.map((category) => (
                  <div
                    onClick={() => setSelectedCategory(category)}
                    key={category.id}
                    className={`flex items-center justify-between w-full px-3 py-2 text-sm text-left transition-colors rounded-md cursor-pointer hover:bg-white/5 ${
                      selectedCategory?.id === category.id && "bg-white/5"
                    }`}
                  >
                    <p>
                      {category.name}{" "}
                      {userWishlist.filter(
                        (w) => w.category?.id === category.id
                      ).length > 0 && (
                        <span>
                          (
                          {
                            userWishlist.filter(
                              (w) => w.category?.id === category.id
                            ).length
                          }
                          )
                        </span>
                      )}
                    </p>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button type='button' className='-mr-1'>
                          <DotsThreeVertical
                            weight='bold'
                            className='text-lg'
                          />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem
                          onClick={(e) => handleOpenRename(e, category)}
                        >
                          <Pen weight='bold' className='w-4 h-4 mr-2' />
                          <span>Rename</span>
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={(e) => handleOpenDeleteCategory(e, category)}
                        >
                          <Trash weight='bold' className='w-4 h-4 mr-2' />
                          <span>Delete</span>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {isMobile ? (
          <Drawer
            open={isRenameDialogOpen}
            onOpenChange={setIsRenameDialogOpen}
          >
            <DrawerContent className='p-5 pt-0 bg-white text-background rounded-t-3xl'>
              <div className='pt-3'>
                <RenameDirectoryContent
                  editedNameCat={editedNameCat}
                  setEditedNameCat={setEditedNameCat}
                  editCategoryName={editCategoryName}
                  handleClose={() => setIsRenameDialogOpen(false)}
                />
              </div>
            </DrawerContent>
          </Drawer>
        ) : (
          <Dialog
            open={isRenameDialogOpen}
            onOpenChange={setIsRenameDialogOpen}
          >
            <DialogContent
              className='sm:max-w-[425px] bg-white text-background !rounded-2xl p-5 gap-0'
              hideCloseButton
            >
              <RenameDirectoryContent
                editedNameCat={editedNameCat}
                setEditedNameCat={setEditedNameCat}
                editCategoryName={editCategoryName}
                handleClose={() => setIsRenameDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        )}

        {isMobile ? (
          <Drawer
            open={isDeleteCategoryDialogOpen}
            onOpenChange={setIsDeleteCategoryDialogOpen}
          >
            <DrawerContent className='p-5 pt-0 bg-white text-background'>
              <div className='pt-3'>
                <DeleteCategoryContent
                  onCancelDeleteCategory={onCancelDeleteCategory}
                  onDeleteCategory={onDeleteCategory}
                />
              </div>
            </DrawerContent>
          </Drawer>
        ) : (
          <AlertDialog
            open={isDeleteCategoryDialogOpen}
            onOpenChange={setIsDeleteCategoryDialogOpen}
          >
            <AlertDialogContent className='p-5 bg-white text-background !rounded-2xl'>
              <DeleteCategoryContent
                onCancelDeleteCategory={onCancelDeleteCategory}
                onDeleteCategory={onDeleteCategory}
              />
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </AccountLayout>
  );
}
